import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addBrandCampaign,
  getBrandCampaignList,
  getBrandCampaignOutletList,
  getBrandCampaignStatus,
  getBrands,
  getClassification,
  getDesignationPosition,
  getDesignations,
  getOutletTypeList,
  getSGPILists,
  getSingleBrandCampaignList,
  publishBrandCampaignStatusChange,
  updateBrandCampaign,
  updateBrandCampaignOutlet,
} from "../repositories/BrandCampaignRepository";
import { toast } from "react-toastify";
import { getOrgUnitsData } from "../../Configuration/Grade Master/repository/GradeMasterRepository";
import { showToast } from "../../../utils/toastUtils";

export const useBrandCampaignListData = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN", params],
    queryFn: () => getBrandCampaignList(params),
    enabled: enable,
    retry: false,
  });
};

export const useBrandCampaignOutletListData = ({
  id,
  params = {},
  enable = false,
}: {
  id?: any;
  params?: Record<string, any>;
  enable?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN_OUTLET", id, params],
    queryFn: () => getBrandCampaignOutletList(id, params),
    enabled: enable,
    retry: false,
  });
};

export const useSingleBrandCampaignListData = ({
  id,
  enable = false,
}: { id?: any; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN", id],
    queryFn: () => getSingleBrandCampaignList(id),
    enabled: enable,
    retry: false,
  });
};

export const useGetDesignationsData = ({
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["DESIGNATION_DATA"],
    queryFn: getDesignations,
    enabled: enable,
    retry: false,
  });
};

export const useGetOutletTypeListData = ({
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN_OUTLET"],
    queryFn: getOutletTypeList,
    enabled: enable,
    retry: false,
  });
};

export const useGetClassificationData = ({
  id,
  enable = false,
}: {
  id?: number | string;
  params?: Record<string, any>;
  enable?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN_CLASSIFICATION", id],
    queryFn: () => getClassification(id),
    enabled: enable,
    retry: false,
  });
};

export const useGetBrandsData = ({
  params,
  enable = false,
}: {
  params?: any;
  enable?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN_BRANDS", params],
    queryFn: () => getBrands(params),
    enabled: enable,
    retry: false,
  });
};

export const useGetBrandCampaignStatusData = ({
  enable = false,
}: { params?: any; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["BRAND_CAMPAIGN_STATUS"],
    queryFn: getBrandCampaignStatus,
    enabled: enable,
    retry: false,
  });
};

export const useGetDesignationPositionData = ({
  params,
  enable = false,
}: {
  params?: any;
  enable?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ["DESIGNATION_POSITION", params],
    queryFn: () => getDesignationPosition(params),
    enabled: enable,
    retry: false,
  });
};

export const useGetSGPIListsData = ({
  params,
  enable = false,
}: {
  params?: any;
  enable?: boolean;
} = {}) => {
  return useQuery({
    queryKey: ["SGPI_LISTS", params],
    queryFn: () => getSGPILists(params),
    enabled: enable,
    retry: false,
  });
};

export const useAddBrandCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addBrandCampaign,
    onSuccess: (data) => {
      showToast.success("Brand Campaign Added Successfully!");
      queryClient.invalidateQueries({ queryKey: ["BRAND_CAMPAIGN"] });
    },
    onError: (error: any) => {
      showToast.warning(error);
    },
  });
};

export const useUpdateBrandCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: any }) =>
      updateBrandCampaign(id, body),
    onSuccess: (data) => {
      showToast.success("Brand Campaign Updated Successfully!");
      queryClient.invalidateQueries({ queryKey: ["BRAND_CAMPAIGN"] });
    },
    onError: (error: any) => {
      showToast.warning(error);
    },
  });
};

export const useGetOrgUnitsDataBrandCampaign = ({
  params = {},
  enable = false,
}: { params?: Record<string, any>; enable?: boolean } = {}) => {
  return useQuery({
    queryKey: ["ORG_UNITS", "SGPI_LISTS", "BRAND_CAMPAIGN_BRANDS"],
    queryFn: getOrgUnitsData,
    enabled: enable,
    retry: false,
  });
};

// updateBrandCampaignOutlet

export const useUpdateBrandCampaignOutlet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: any }) =>
      updateBrandCampaignOutlet(id, body),
    onSuccess: (data) => {
      showToast.success("Brand Campaign Outlet Updated Successfully!");
      queryClient.invalidateQueries({ queryKey: ["BRAND_CAMPAIGN_OUTLET"] });
    },
    onError: (error: any) => {
      showToast.warning(error);
    },
  });
};

export const usePublishBrandCampaignStatusChange = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: any }) =>
      publishBrandCampaignStatusChange(id, body),
    onSuccess: (data) => {
      showToast.success("Brand Campaign Published Successfully!");
      queryClient.invalidateQueries({ queryKey: ["BRAND_CAMPAIGN"] });
    },
    onError: (error: any) => {
      showToast.warning(error);
    },
  });
};
